import { describe_error } from '../utils/ErrorDescriber';
import React, { useState, useRef, useEffect } from 'react';
import { Button, Spinner, Form, Modal } from 'react-bootstrap';
import axios from 'axios';
import Markdown from 'react-markdown';

import Constants from '../Constants';
import { useAuth } from '../AuthContext';
import { is_expired_session_error } from '../utils/AuthInterceptor';
import '../App.css';

// The AEO Assistant chat: the conversation, the history of the user's past chats, New Chat and Delete. Used by the
// full-page chat (/chat) and by the collapsible panel (AssistantLayout) on the analysis, site and site page views.
//
// Props:
//   analysis_context  { analysis_id, url, kind } of the analysis on screen (kind 'site' or 'page'), sent with each
//                     message so the assistant knows which analysis "this page" or "this site" means (the panel
//                     passes it; the full page does not)
//   on_close          when given, a Close button is shown that calls it (the panel uses it to collapse)
//   pending_message   { key, analysis_id, kind, text } from the Ask AI button: once the chat has loaded, the user's chat
//                     about that analysis is opened, or, when there is none, a new chat is started with text
//   on_pending_message_sent  called once pending_message has been acted on
//   view_key          given by the panel only: the view it sits in (page_analysis:<id>, site_analysis:<id> or
//                     site_page:<id>), null while the view's analysis is still loading. Each view remembers its own
//                     chat on the server (GET and PUT /api/view_chat/:view_key): opening the panel in a view reopens
//                     the chat last used there, or an empty new chat when the view has none, which is never sent to
//                     the assistant until the user writes in it
//
// It works like claude.ai: a chat is created by its first message, New Chat starts an empty one, past chats are
// listed in the history and reopened from there, and a chat can be deleted. In a view, the chat in use is recorded as
// the view's chat; the full page keeps its current chat's id in localStorage (only the id: the DB holds the
// conversation).

// localStorage key holding ONLY the current conversation id: a lookup key into the DB, not authoritative state.
const c_ConversationIdKey = 'aeoaudited-chat-conversation-id';

const c_RequestFailedText = 'Request failed. Please try again.';

// Monotonic id source for messages added on this page, so a reply or an error is tied to the request that produced
// it without relying on array position.
var message_id_counter = 0;
const next_message_id = ()=>{
	++message_id_counter;
	return 'local-' + message_id_counter;
};

// Mark the user message with the given id settled and insert its assistant reply directly after it. Returns a new
// array; does not mutate the input.
const settle_message = (message_list, user_message_id, assistant_message)=>{
	const next_messages = [];

	for(var message_index = 0; message_index < message_list.length; ++message_index){
		const message = message_list[message_index];

		if(message.id === user_message_id){
			next_messages.push({ ...message, status: 'done' });
			next_messages.push(assistant_message);
		}
		else{
			next_messages.push(message);
		}
	}

	return next_messages;
};

// Mark the user message with the given id as failed, attaching its own error text. Returns a new array.
const fail_message = (message_list, user_message_id, error_text)=>{
	const next_messages = [];

	for(var message_index = 0; message_index < message_list.length; ++message_index){
		const message = message_list[message_index];

		if(message.id === user_message_id){
			next_messages.push({ ...message, status: 'done', error: error_text });
		}
		else{
			next_messages.push(message);
		}
	}

	return next_messages;
};

// The messages a conversation restore returns, minus the rows with no text: a tool-results turn is stored as a
// message with an empty text, and has nothing to show.
const build_restored_messages = (restored_messages)=>{
	const rebuilt = [];

	for(var message_index = 0; message_index < restored_messages.length; ++message_index){
		const message = restored_messages[message_index];
		if(!message.text){
			continue;
		}

		rebuilt.push({
			id: message.id,
			role: message.role,
			text: message.text,
			status: 'done',
			error: null
		});
	}

	return rebuilt;
};

const format_activity_date = (date_text)=>{
	if(!date_text){
		return '';
	}

	return new Date(date_text).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const ChatPanel = ({ analysis_context, on_close, pending_message, on_pending_message_sent, view_key })=>{
	const { token } = useAuth();

	// The panel passes view_key, null or a key; the full page passes none.
	const is_view_chat = (view_key !== undefined);

	// The view the chat in use belongs to, read when recording it, so a record always goes to the view on screen.
	const view_key_ref = useRef(view_key);
	view_key_ref.current = view_key;

	// The conversation id last recorded for the view, so the same id is not sent again.
	const recorded_view_chat_ref = useRef(undefined);

	const [messages, set_messages] = useState([]);
	const [input_text, set_input_text] = useState('');
	const [in_flight_count, set_in_flight_count] = useState(0);
	const [loading_conversation, set_loading_conversation] = useState(true);
	const [load_error, set_load_error] = useState(null);

	const [show_history, set_show_history] = useState(false);
	const [conversations, set_conversations] = useState([]);
	const [loading_history, set_loading_history] = useState(false);
	const [history_error, set_history_error] = useState(null);

	const [conversation_to_delete, set_conversation_to_delete] = useState(null);
	const [deleting, set_deleting] = useState(false);
	const [delete_error, set_delete_error] = useState(null);

	// The current conversation. A ref, not state, so a send always reads the current id rather than a stale closure
	// value. Null for a new chat that has no message yet: the backend creates the conversation on its first message.
	const conversation_id_ref = useRef(null);
	const messages_end_ref = useRef(null);

	// Counts what the user has switched the panel to (opening a chat, starting a new chat). A chat load only applies
	// its result if nothing newer has happened since it started, so a slow load can never put an old chat back over
	// the one the user has moved to.
	const chat_switch_count_ref = useRef(0);

	const begin_chat_switch = ()=>{
		chat_switch_count_ref.current = chat_switch_count_ref.current + 1;
		return chat_switch_count_ref.current;
	};

	const is_latest_chat_switch = (switch_number)=>{
		return (chat_switch_count_ref.current === switch_number);
	};

	const build_headers = ()=>{
		return { Authorization: 'Bearer ' + token };
	};

	// The last message on screen, so a reply can be shown from its first line.
	const last_message_ref = useRef(null);

	// When the newest message is the assistant's reply, the chat scrolls to the reply's first line, so it is read from
	// the start. Otherwise, a message just sent or the Thinking line, it scrolls to the bottom.
	useEffect(()=>{
		const last_message = (messages.length > 0) ? messages[messages.length - 1] : null;
		const reply_arrived = last_message && (last_message.role === 'assistant') && (in_flight_count === 0);

		if(reply_arrived && last_message_ref.current){
			last_message_ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
			return;
		}

		if(messages_end_ref.current){
			messages_end_ref.current.scrollIntoView({ behavior: 'smooth' });
		}
	}, [messages, in_flight_count]);

	// Record the chat in use as the view's chat on the server; null forgets the view's chat, so the view opens a new
	// one next time. A failed record is logged only: the chat itself is unaffected.
	const record_view_chat = async(conversation_id)=>{
		const current_view_key = view_key_ref.current;
		if(!current_view_key || (recorded_view_chat_ref.current === conversation_id)){
			return;
		}

		recorded_view_chat_ref.current = conversation_id;

		try{
			await axios.put(Constants.SERVER_URL + '/api/view_chat/' + encodeURIComponent(current_view_key), { conversation_id }, { headers: build_headers() });
		}
		catch(error){
			console.log('ChatPanel/record_view_chat - ERROR: ', describe_error(error));
		}
	};

	const set_current_conversation_id = (conversation_id)=>{
		conversation_id_ref.current = conversation_id;

		if(is_view_chat){
			record_view_chat(conversation_id);
			return;
		}

		if(conversation_id){
			window.localStorage.setItem(c_ConversationIdKey, conversation_id);
		}
		else{
			window.localStorage.removeItem(c_ConversationIdKey);
		}
	};

	// Load one of the user's conversations and make it the current chat. A conversation the server does not know
	// for this user (deleted, or another user's) leaves an empty new chat instead. The result is dropped when the
	// user has switched chats again while it loaded.
	const open_conversation = async(conversation_id)=>{
		const switch_number = begin_chat_switch();

		set_loading_conversation(true);
		set_load_error(null);

		try{
			const response = await axios.get(Constants.SERVER_URL + '/api/conversation/' + encodeURIComponent(conversation_id), { headers: build_headers() });
			if(!is_latest_chat_switch(switch_number)){
				return;
			}

			const restored_messages = (response.data && response.data.messages) ? response.data.messages : [];

			set_current_conversation_id(response.data.conversation_id);
			set_messages(build_restored_messages(restored_messages));
		}
		catch(error){
			if(!is_latest_chat_switch(switch_number)){
				return;
			}

			const not_found = error.response && (error.response.status === 404);
			if(not_found){
				set_current_conversation_id(null);
				set_messages([]);
			}
			else
			if(!is_expired_session_error(error)){
				console.log('ChatPanel/open_conversation - ERROR: ', describe_error(error));
				set_load_error('The chat could not be loaded. Please try again.');
			}
		}
		finally{
			if(is_latest_chat_switch(switch_number)){
				set_loading_conversation(false);
			}
		}
	};

	// The full page, on mount: reopen the current chat when there is one; otherwise start with an empty new chat.
	useEffect(()=>{
		if(is_view_chat){
			return;
		}

		const stored_id = window.localStorage.getItem(c_ConversationIdKey);

		if(stored_id){
			open_conversation(stored_id);
			return;
		}

		set_loading_conversation(false);
	}, []);

	// The panel, whenever the view it sits in is known or changes: reopen the chat last used in that view, or start
	// an empty new chat when the view has none. Nothing is sent to the assistant.
	useEffect(()=>{
		if(!is_view_chat){
			return;
		}

		open_view_chat(view_key);
	}, [view_key]);

	const open_view_chat = async(current_view_key)=>{
		const switch_number = begin_chat_switch();

		conversation_id_ref.current = null;
		set_messages([]);
		set_load_error(null);
		set_show_history(false);

		if(!current_view_key){
			recorded_view_chat_ref.current = undefined;
			set_loading_conversation(false);
			return;
		}

		set_loading_conversation(true);

		var conversation_id = null;
		try{
			const response = await axios.get(Constants.SERVER_URL + '/api/view_chat/' + encodeURIComponent(current_view_key), { headers: build_headers() });
			conversation_id = response.data.conversation_id;
		}
		catch(error){
			if(!is_latest_chat_switch(switch_number)){
				return;
			}

			console.log('ChatPanel/open_view_chat - ERROR: ', describe_error(error));
			if(!is_expired_session_error(error)){
				set_load_error('The chat for this page could not be loaded. Please try again.');
			}
			set_loading_conversation(false);
			return;
		}

		if(!is_latest_chat_switch(switch_number)){
			return;
		}

		recorded_view_chat_ref.current = conversation_id;

		if(conversation_id){
			await open_conversation(conversation_id);
			return;
		}

		set_loading_conversation(false);
	};

	const load_history = async()=>{
		set_loading_history(true);
		set_history_error(null);

		try{
			const response = await axios.get(Constants.SERVER_URL + '/api/conversations', { headers: build_headers() });
			set_conversations((response.data && response.data.conversations) ? response.data.conversations : []);
		}
		catch(error){
			console.log('ChatPanel/load_history - ERROR: ', describe_error(error));
			if(!is_expired_session_error(error)){
				set_history_error('Your chats could not be loaded. Please try again.');
			}
		}
		finally{
			set_loading_history(false);
		}
	};

	const handle_toggle_history = ()=>{
		const next_show_history = !show_history;
		set_show_history(next_show_history);

		if(next_show_history){
			load_history();
		}
	};

	const handle_new_chat = ()=>{
		begin_chat_switch();

		set_current_conversation_id(null);
		set_messages([]);
		set_load_error(null);
		set_loading_conversation(false);
		set_show_history(false);
	};

	const handle_select_conversation = (conversation_id)=>{
		set_show_history(false);
		open_conversation(conversation_id);
	};

	const handle_confirm_delete = async()=>{
		if(!conversation_to_delete){
			return;
		}

		set_deleting(true);
		set_delete_error(null);

		try{
			await axios.delete(Constants.SERVER_URL + '/api/conversation/' + encodeURIComponent(conversation_to_delete.conversation_id), { headers: build_headers() });

			if(conversation_id_ref.current === conversation_to_delete.conversation_id){
				handle_new_chat();
			}

			set_conversations((current)=>{ return current.filter((conversation)=>{ return conversation.conversation_id !== conversation_to_delete.conversation_id; }); });
			set_conversation_to_delete(null);
		}
		catch(error){
			console.log('ChatPanel/handle_confirm_delete - ERROR: ', describe_error(error));
			if(!is_expired_session_error(error)){
				set_delete_error('The chat could not be deleted. Please try again.');
			}
		}
		finally{
			set_deleting(false);
		}
	};

	const handle_send = async()=>{
		const trimmed = input_text.trim();
		if(trimmed.length === 0){
			return;
		}

		set_input_text('');
		await send_message(trimmed);
	};

	// A request handed in by the page (the analysis page's Ask AI button), acted on once the chat has loaded. Each
	// request is acted on once: its key is remembered.
	const handled_pending_key_ref = useRef(null);

	useEffect(()=>{
		if(!pending_message || loading_conversation){
			return;
		}

		if(handled_pending_key_ref.current === pending_message.key){
			return;
		}

		handled_pending_key_ref.current = pending_message.key;

		open_analysis_chat(pending_message);

		if(on_pending_message_sent){
			on_pending_message_sent();
		}
	}, [pending_message, loading_conversation]);

	// Ask AI: open the user's chat about the analysis when they have one. When they do not, start a new chat and send
	// the request, so the new chat's opening message names the analysis and is found next time.
	const open_analysis_chat = async(request)=>{
		const switch_number = begin_chat_switch();

		set_show_history(false);
		set_load_error(null);
		set_loading_conversation(true);

		var conversation_id = null;

		try{
			// kind and the question let the backend match a site's stored AI site analysis to the current prompts.
			const lookup_params = {};
			if(request.kind){
				lookup_params.kind = request.kind;
				lookup_params.question = request.text;
			}

			const response = await axios.get(Constants.SERVER_URL + '/api/analysis_chat/' + encodeURIComponent(request.analysis_id), { params: lookup_params, headers: build_headers() });
			conversation_id = response.data.conversation_id;
		}
		catch(error){
			if(!is_latest_chat_switch(switch_number)){
				return;
			}

			console.log('ChatPanel/open_analysis_chat - ERROR: ', describe_error(error));
			if(!is_expired_session_error(error)){
				set_load_error('The chat for this analysis could not be opened. Please try again.');
			}
			set_loading_conversation(false);
			return;
		}

		if(!is_latest_chat_switch(switch_number)){
			return;
		}

		if(conversation_id){
			await open_conversation(conversation_id);
			return;
		}

		handle_new_chat();
		send_message(request.text, true);
	};

	// Send one message in the current chat (a new chat's first message creates its conversation). ask_ai marks the
	// Ask AI button's request, so the backend records its reply as the analysis's AI analysis.
	const send_message = async(trimmed, ask_ai)=>{
		// The chat this message is sent in: its reply only becomes the current chat if the user is still in it.
		const switch_number = chat_switch_count_ref.current;
		const message_id = next_message_id();
		const user_message = { id: message_id, role: 'user', text: trimmed, status: 'pending', error: null };

		set_messages((current)=>{ return current.concat([user_message]); });
		set_in_flight_count((current)=>{ return current + 1; });

		const request_body = { message: trimmed };
		if(conversation_id_ref.current){
			request_body.conversation_id = conversation_id_ref.current;
		}

		if(analysis_context && analysis_context.analysis_id && analysis_context.url){
			request_body.analysis_context = { analysis_id: analysis_context.analysis_id, url: analysis_context.url, kind: analysis_context.kind };
		}

		if(ask_ai){
			request_body.ask_ai = true;
		}

		try{
			const response = await axios.post(Constants.SERVER_URL + '/api/chat', request_body, { headers: build_headers() });

			// A new chat's conversation is created by its first message; keep its id for the next message, unless the
			// user has moved to another chat or view while it was sent.
			if(is_latest_chat_switch(switch_number)){
				set_current_conversation_id(response.data.conversation_id);
			}

			const assistant_message = {
				id: next_message_id(),
				role: 'assistant',
				text: response.data.assistant_text ? response.data.assistant_text : '',
				status: 'done',
				error: null
			};

			set_messages((current)=>{ return settle_message(current, message_id, assistant_message); });
		}
		catch(error){
			console.log('ChatPanel/handle_send - ERROR: ', describe_error(error));
			if(!is_expired_session_error(error)){
				set_messages((current)=>{ return fail_message(current, message_id, c_RequestFailedText); });
			}
		}
		finally{
			set_in_flight_count((current)=>{ return current - 1; });
		}
	};

	const handle_key_down = (event)=>{
		if((event.key === 'Enter') && !event.shiftKey){
			event.preventDefault();
			handle_send();
		}
	};

	const render_header = ()=>{
		const busy = loading_conversation || (in_flight_count > 0);

		return (
			<div className="chat-header">
				<h1 className="chat-title">AEO Assistant</h1>
				<div className="chat-header-buttons">
					<Button
						className="chat-header-button"
						onClick={handle_toggle_history}
						disabled={busy}
					>
						{show_history ? 'Back to Chat' : 'Chats'}
					</Button>
					<Button
						className="chat-header-button"
						onClick={handle_new_chat}
						disabled={busy}
					>
						New Chat
					</Button>
					{on_close ? (
						<Button
							className="chat-header-button chat-close-button"
							onClick={on_close}
							aria-label="Close the assistant"
						>
							Close
						</Button>
					) : null}
				</div>
			</div>
		);
	};

	const render_history_item = (conversation)=>{
		const is_current = (conversation.conversation_id === conversation_id_ref.current);
		const item_class = is_current ? 'chat-history-item chat-history-item-current' : 'chat-history-item';

		return (
			<div className={item_class} key={conversation.conversation_id}>
				<button
					type="button"
					className="chat-history-open"
					onClick={()=>{ handle_select_conversation(conversation.conversation_id); }}
				>
					<span className="chat-history-title">{conversation.title}</span>
					<span className="chat-history-date">{format_activity_date(conversation.last_activity_at)}</span>
				</button>
				<button
					type="button"
					className="chat-history-delete"
					onClick={()=>{ set_delete_error(null); set_conversation_to_delete(conversation); }}
					aria-label={'Delete chat: ' + conversation.title}
				>
					Delete
				</button>
			</div>
		);
	};

	const render_history = ()=>{
		if(loading_history){
			return (
				<div className="chat-history-status">
					<Spinner animation="border" size="sm" />
				</div>
			);
		}

		if(history_error){
			return (<div className="chat-load-error">{history_error}</div>);
		}

		if(conversations.length === 0){
			return (<div className="chat-empty">You have no chats yet.</div>);
		}

		return (
			<div className="chat-history-list">
				{conversations.map((conversation)=>{ return render_history_item(conversation); })}
			</div>
		);
	};

	// The assistant replies in Markdown, which is rendered; raw HTML in a reply is dropped. A user's message is shown
	// exactly as typed.
	const render_message_text = (message, is_user)=>{
		if(is_user){
			return (
				<div className="chat-message-text">{message.text}</div>
			);
		}

		return (
			<div className="chat-message-text chat-message-markdown">
				<Markdown skipHtml={true}>{message.text}</Markdown>
			</div>
		);
	};

	const render_message = (message, is_last)=>{
		const is_user = (message.role === 'user');
		const row_class = is_user ? 'chat-message-row chat-message-row-user' : 'chat-message-row chat-message-row-assistant';
		const bubble_class = is_user ? 'chat-message-bubble chat-message-bubble-user' : 'chat-message-bubble chat-message-bubble-assistant';

		return (
			<div className={row_class} key={message.id} ref={is_last ? last_message_ref : null}>
				<div className={bubble_class}>
					{render_message_text(message, is_user)}
					{message.error ? <div className="chat-message-error">{message.error}</div> : null}
				</div>
			</div>
		);
	};

	const render_empty = ()=>{
		return (
			<div className="chat-empty">
				Ask about your sites and analyses: which pages scored lowest, what a weakness means, or which fixes to do first.
			</div>
		);
	};

	const render_messages = ()=>{
		if(loading_conversation){
			return (
				<div className="chat-history-status">
					<Spinner animation="border" size="sm" />
				</div>
			);
		}

		if(load_error){
			return (<div className="chat-load-error">{load_error}</div>);
		}

		if(messages.length === 0){
			return render_empty();
		}

		return (
			<React.Fragment>
				{messages.map((message, message_index)=>{ return render_message(message, message_index === (messages.length - 1)); })}
			</React.Fragment>
		);
	};

	const render_status = ()=>{
		if(in_flight_count === 0){
			return null;
		}

		return (
			<div className="chat-message-row chat-message-row-assistant">
				<div className="chat-message-bubble chat-message-bubble-assistant chat-message-bubble-status">
					<Spinner animation="border" size="sm" />
					<span className="chat-status-text">Thinking...</span>
				</div>
			</div>
		);
	};

	const render_input_bar = ()=>{
		// A new chat has no conversation until its first message returns, so a second message waits for it rather
		// than creating a second conversation.
		const waiting_for_new_conversation = !conversation_id_ref.current && (in_flight_count > 0);
		const send_disabled = (input_text.trim().length === 0) || loading_conversation || waiting_for_new_conversation;

		return (
			<div className="chat-input-bar">
				<Form.Control
					as="textarea"
					rows={2}
					className="chat-input"
					placeholder="Ask about your sites and analyses..."
					value={input_text}
					onChange={(event)=>{ set_input_text(event.target.value); }}
					onKeyDown={handle_key_down}
				/>
				<Button
					className="chat-send-button"
					onClick={handle_send}
					disabled={send_disabled}
				>
					Send
				</Button>
			</div>
		);
	};

	const render_delete_modal = ()=>{
		return (
			<Modal show={conversation_to_delete !== null} onHide={()=>{ set_conversation_to_delete(null); }} centered>
				<Modal.Header closeButton>
					<Modal.Title>Delete chat</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p className="chat-delete-text">
						{'Delete "' + (conversation_to_delete ? conversation_to_delete.title : '') + '"? This permanently removes the chat and all its messages.'}
					</p>
					{delete_error ? <div className="chat-message-error">{delete_error}</div> : null}
				</Modal.Body>
				<Modal.Footer>
					<Button
						className="chat-header-button"
						onClick={()=>{ set_conversation_to_delete(null); }}
						disabled={deleting}
					>
						Cancel
					</Button>
					<Button
						className="chat-delete-confirm-button"
						onClick={handle_confirm_delete}
						disabled={deleting}
					>
						{deleting ? 'Deleting...' : 'Delete'}
					</Button>
				</Modal.Footer>
			</Modal>
		);
	};

	const render_body = ()=>{
		if(show_history){
			return (
				<div className="chat-messages">
					{render_history()}
				</div>
			);
		}

		return (
			<React.Fragment>
				<div className="chat-messages">
					{render_messages()}
					{render_status()}
					<div ref={messages_end_ref} />
				</div>
				{render_input_bar()}
			</React.Fragment>
		);
	};

	return (
		<div className="chat-panel">
			{render_header()}
			{render_body()}
			{render_delete_modal()}
		</div>
	);
};

export default ChatPanel;
